/**
 * StudentDetailScreen — student header + goals list + sessions list.
 *
 * v1.0-A: read-only display + edit / archive actions. Goals and
 * sessions are shown as flat lists with status pills. Adding /
 * editing is done through the separate form screens routed by
 * App.tsx.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import { PracticeRollupView } from './student/PracticeRollupView';
import { ActiveHandoffLinks } from './student/ActiveHandoffLinks';
import { StudentAvatar } from '../lib/effectiveslp/StudentAvatar';
import {
  formatShortDate,
  labelForGoalStatus,
  pickLastSession,
  pickNextSession,
} from '../lib/effectiveslp/domain';
import { CalendarIcon } from '../components/icons/CalendarIcon';
import { CheckIcon } from '../components/icons/CheckIcon';
import { NotesIcon } from '../components/icons/NotesIcon';
import { RefreshIcon } from '../components/icons/RefreshIcon';

interface Props {
  studentId: string;
  onBack: () => void;
  onEdit: () => void;
  onAddGoal: () => void;
  onEditGoal: (goalId: string) => void;
  onScheduleSession: () => void;
  // ESLP-16 parity: tapping an existing session row opens the
  // SessionPlannerScreen for that session. Without this the only
  // way to reach the planner is "Schedule a session" (which always
  // creates a new one) — existing sessions are unreachable.
  onOpenSession: (sessionId: string) => void;
}

interface Student {
  id: string;
  first_name: string;
  age_years: number | null;
  disorder_category: string;
  notes: string | null;
  archived: boolean;
}

interface Goal {
  id: string;
  area: string;
  goal_text: string;
  target_accuracy: number | null;
  current_accuracy: number | null;
  status: string;
}

interface Session {
  id: string;
  scheduled_at: string;
  completed_at: string | null;
  status: string;
  session_type: string;
}

export function StudentDetailScreen({
  studentId,
  onBack,
  onEdit,
  onAddGoal,
  onEditGoal,
  onScheduleSession,
  onOpenSession,
}: Props): ReactElement {
  const [student, setStudent] = useState<Student | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [archivedBusy, setArchivedBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [sRes, gRes, sessRes] = await Promise.all([
          supabase
            .from('students')
            .select('id, first_name, age_years, disorder_category, notes, archived')
            .eq('id', studentId)
            .maybeSingle(),
          supabase
            .from('goals')
            .select('id, area, goal_text, target_accuracy, current_accuracy, status')
            .eq('student_id', studentId)
            .order('created_at', { ascending: false }),
          supabase
            .from('sessions')
            .select('id, scheduled_at, completed_at, status, session_type')
            .eq('student_id', studentId)
            .order('scheduled_at', { ascending: false }),
        ]);
        if (cancelled) return;
        if (sRes.error) {
          setError(sRes.error.message);
          return;
        }
        setStudent((sRes.data as Student | null) ?? null);
        setGoals((gRes.data as Goal[] | null) ?? []);
        setSessions((sessRes.data as Session[] | null) ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load student.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  async function toggleArchive(): Promise<void> {
    if (!student) return;
    setArchivedBusy(true);
    try {
      const nextArchived = !student.archived;
      const { error: uErr } = await supabase
        .from('students')
        .update({ archived: nextArchived })
        .eq('id', studentId);
      if (uErr) {
        setError(uErr.message);
        return;
      }
      setStudent({ ...student, archived: nextArchived });
      onBack();
    } finally {
      setArchivedBusy(false);
    }
  }

  if (!student && !error) {
    return (
      <main className="screen">
        <p className="tagline">Loading student…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="screen">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back
        </button>
        <p className="error">{error}</p>
      </main>
    );
  }

  return (
    <main className="screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to caseload
        </button>
        {student && (
          <div className="student-hero">
            <StudentAvatar
              firstName={student.first_name}
              seed={student.id}
              size={64}
            />
            <div className="student-hero-text">
              <h1>{student.first_name}</h1>
              <p className="tagline">
                {student.age_years != null ? `Age ${student.age_years} · ` : ''}
                {student.disorder_category.replace(/_/g, ' ')}
                {student.archived ? ' · Archived' : ''}
              </p>
            </div>
          </div>
        )}
        {!student && <h1>Student</h1>}
      </header>

      {/* Notes card — iOS parity: AppCard with note.text icon. */}
      {student?.notes && (
        <div className="card student-notes-card">
          <NotesIcon size={18} className="student-notes-icon" />
          <p className="student-notes-text">{student.notes}</p>
        </div>
      )}

      {/* Next/Last session quick-action — iOS parity: heroSection
          shows the most recent session as a tappable AppCard that
          opens the planner.

          #138: previously rendered `sessions[0]` (most-recent by
          scheduled_at desc) and labelled by `completed_at != null`,
          which mislabelled a missed past session as "Next" when the
          student also had a future session. Pick the row explicitly:
          upcoming not-yet-completed → "Next session"; else most
          recent past → "Last session". Card is hidden when neither
          exists (no completed *and* no sessions). */}
      {(() => {
        const now = Date.now();
        const nextSession = pickNextSession(sessions, now);
        const lastSession = pickLastSession(sessions, now);
        const isNext = nextSession !== undefined;
        const featured = (nextSession ?? lastSession) as Session | undefined;
        if (!featured) return null;
        return (
          <button
            type="button"
            className="card next-session-card"
            onClick={() => onOpenSession(featured.id)}
          >
            <span className="next-session-icon">
              {isNext ? <CalendarIcon size={20} /> : <CheckIcon size={20} />}
            </span>
            <span className="next-session-text">
              <span className="next-session-eyebrow">
                {isNext ? 'Next session' : 'Last session'}
              </span>
              <span className="next-session-date">
                {formatShortDate(featured.scheduled_at)}
              </span>
            </span>
            <span className="caseload-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        );
      })()}

      <section className="form-actions">
        <button
          type="button"
          className="snap-button"
          onClick={onScheduleSession}
        >
          Schedule a session
        </button>
        <div className="button-row">
          <button type="button" className="btn-secondary" onClick={onAddGoal}>
            + Add goal
          </button>
          <button type="button" className="btn-secondary" onClick={onEdit}>
            Edit student
          </button>
        </div>
        <button
          type="button"
          className="link-button"
          disabled={archivedBusy}
          onClick={() => void toggleArchive()}
        >
          {student?.archived ? 'Unarchive student' : 'Archive student'}
        </button>
      </section>

      {/* Initial Evaluation card — shown when an initial_eval session
          exists for this student. Tapping opens the structured eval
          viewer (ESLP-64). */}
      {sessions.some((s) => s.session_type === 'initial_eval') && (
        <section>
          <h2 className="onboarding-eyebrow">Evaluation</h2>
          {sessions
            .filter((s) => s.session_type === 'initial_eval')
            .map((s) => (
              <button
                type="button"
                key={s.id}
                className="card next-session-card"
                onClick={() => onOpenSession(s.id)}
              >
                <span className="next-session-icon">
                  <NotesIcon size={20} />
                </span>
                <span className="next-session-text">
                  <span className="next-session-eyebrow">
                    Initial Evaluation
                  </span>
                  <span className="next-session-date">
                    {formatShortDate(s.scheduled_at)}
                  </span>
                </span>
                <span className="caseload-chevron" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
        </section>
      )}

      <section>
        <h2 className="onboarding-eyebrow">Goals ({goals.length})</h2>
        {goals.length === 0 && (
          <p className="tagline">No goals yet. Tap "Add goal."</p>
        )}
        {goals.map((g) => (
          <button
            type="button"
            key={g.id}
            className="caseload-row"
            onClick={() => onEditGoal(g.id)}
          >
            <span className="caseload-row-name">{g.area}</span>
            <span className="caseload-row-meta">{g.goal_text}</span>
            <span className="tagline">
              {labelForGoalStatus(g.status)}
              {g.target_accuracy != null
                ? ` · Target ${g.target_accuracy}%`
                : ''}
              {g.current_accuracy != null
                ? ` · Current ${g.current_accuracy}%`
                : ''}
            </span>
          </button>
        ))}
      </section>

      <section>
        <h2 className="onboarding-eyebrow">Sessions ({sessions.length})</h2>
        {sessions.length === 0 && (
          <p className="tagline">No sessions yet.</p>
        )}
        {sessions.map((s) => (
          <button
            type="button"
            key={s.id}
            className="caseload-row"
            onClick={() => onOpenSession(s.id)}
          >
            <div className="caseload-row-body">
              <div className="caseload-row-main">
                <span className="caseload-row-name">
                  {formatShortDate(s.scheduled_at)}
                </span>
                <span className="caseload-chip">
                  {s.session_type === 'initial_eval' ? (
                    <>
                      <NotesIcon size={12} aria-hidden="true" /> Eval
                    </>
                  ) : s.session_type === 'reeval' ? (
                    <>
                      <RefreshIcon size={12} aria-hidden="true" /> Re-eval
                    </>
                  ) : s.completed_at != null ? (
                    <>
                      <CheckIcon size={12} aria-hidden="true" /> Done
                    </>
                  ) : (
                    'Therapy'
                  )}
                </span>
              </div>
              <span className="caseload-row-meta">
                {s.completed_at ? 'Completed' : 'Scheduled'}
              </span>
            </div>
            <span className="caseload-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </section>

      <PracticeRollupView
        studentId={studentId}
        studentName={student?.first_name ?? 'this student'}
        goals={goals}
        sessionId={sessions[0]?.id}
      />

      <ActiveHandoffLinks studentId={studentId} />
    </main>
  );
}
